"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { createClient } from "@/lib/supabase/client";
import { useState, useEffect } from "react";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading } = useAuth();
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUserProfile();
      checkAdminStatus();
    }
  }, [user]);

  const fetchUserProfile = async () => {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from("user_profiles")
        .select("full_name, status")
        .eq("id", user?.id)
        .single();

      setUserProfile(data);
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
    }
  };

  const checkAdminStatus = async () => {
    try {
      const supabase = createClient();
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const adminStatus = authUser?.user_metadata?.role === "admin" || authUser?.app_metadata?.role === "admin";
      setIsAdmin(adminStatus);
    } catch (error) {
      console.error("Failed to check admin status:", error);
    }
  };

  const handleLogout = async () => {
    try {
      const supabase = createClient();
      await supabase.auth.signOut();
      router.push("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Show login/signup for unauthenticated users
  if (!loading && !user) {
    return (
      <header className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/login" className="flex items-center gap-2.5 group">
            <AppLogo size={32} />
            <div>
              <span className="font-heading font-700 text-navy text-base leading-tight tracking-tight block">
                PropertyData<span className="text-teal">TT</span>
              </span>
              <span className="text-2xs text-muted leading-none tracking-widest uppercase font-body hidden sm:block">
                Agent Portal
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <Link
              href="/login"
              className="px-4 py-2 text-sm font-medium text-navy hover:text-teal transition-colors"
            >
              Log In
            </Link>
            <Link
              href="/signup"
              className="px-4 py-2 bg-teal text-white rounded text-sm font-medium hover:bg-teal-600 transition-colors"
            >
              Sign Up
            </Link>
          </div>
        </div>
      </header>
    );
  }

  // Show full navigation for authenticated users
  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-border lg:pl-64">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => {
                const sidebar = document.getElementById('app-sidebar');
                if (sidebar) sidebar.classList.toggle('-translate-x-full');
              }}
              className="p-2 -ml-2 rounded-lg text-muted hover:text-navy hover:bg-navy-50 lg:hidden"
            >
              <Icon name="Bars3Icon" size={24} />
            </button>

            {/* Logo - only mobile */}
            <Link href="/search-page" className="flex items-center gap-2.5 group lg:hidden">
              <AppLogo size={28} />
              <span className="font-heading font-700 text-navy text-sm leading-tight tracking-tight block">
                PropertyData<span className="text-teal">TT</span>
              </span>
            </Link>
          </div>

          {/* User area */}
          <div className="flex items-center gap-3">
            {userProfile && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-sand rounded-md border border-border">
                <div className="w-6 h-6 rounded-full bg-navy flex items-center justify-center">
                  <span className="text-white text-2xs font-heading font-bold">
                    {userProfile?.full_name?.split(" ")?.map((n: string) => n[0])?.join("")?.toUpperCase()}
                  </span>
                </div>
                <span className="text-sm text-navy font-medium hidden sm:block">
                  {userProfile?.full_name?.split(" ")[0]}
                </span>
                {userProfile?.status === "approved" && (
                  <span className="text-2xs bg-teal text-white px-1.5 py-0.5 rounded font-heading font-semibold tracking-wide">
                    {isAdmin ? "ADMIN" : "AGENT"}
                  </span>
                )}
              </div>
            )}
            <button
              onClick={handleLogout}
              className="p-2 rounded text-muted hover:text-navy hover:bg-navy-50 transition-colors"
              title="Log out"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={18} />
            </button>
          </div>
        </div>
      </header>
    </>
  );
}